import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { MeadowAuthForm } from '@/components/blocks/auth/MeadowAuthForm';
import { useTrackLoginScreenOpened } from '@/components/blocks/auth/use-track-login-screen-opened';
import { PasswordLoginForm } from '@/components/blocks/AuthForm';
import { CometlyPixel } from '@/components/CometlyPixel';
import { getEnvVar } from '@/env';
import { electronApis } from '@/features/electron-api';
import {
  initiateSocialOAuthRedirect,
  isPasswordLoginEmail,
  lookupSsoConnection,
  resolveDeepLinkProtocol,
  sendOtpCode,
  signInWithPassword,
  verifyOtpCode,
  type Auth0LoginConfig,
} from '@/features/login';
import { trackEvent } from '@/utils/analytics';
import { oncePerArgs } from '@/utils/jsUtils';
import { getLastLoginEmail, setLastLoginEmail } from '@/utils/localStorage';
import { classifyOtpError } from '@/utils/otp-errors';
import { getDeepLinkProtocol } from '@/utils/platform';

const auth0Config: Auth0LoginConfig = {
  domain: getEnvVar('VITE_AUTH0_DOMAIN'),
  clientId: getEnvVar('VITE_AUTH0_CLIENT_ID'),
  audience: getEnvVar('VITE_AUTH0_AUDIENCE'),
};

const trackLoginCodeScreenViewed = oncePerArgs((email: string) =>
  trackEvent('LOGIN_CODE_SCREEN_VIEWED', { email })
);

export function DesktopLoginScreen() {
  const navigate = useNavigate();
  const lastUsedEmail = getLastLoginEmail();
  const urlParams = new URLSearchParams(window.location.search);
  const search = {
    state: urlParams.get('state') || undefined,
    code_verifier: urlParams.get('code_verifier') || undefined,
    code: urlParams.get('code') || undefined,
    error: urlParams.get('error') || undefined,
    error_description: urlParams.get('error_description') || undefined,
    email: urlParams.get('email') || undefined,
    login_attempt: urlParams.get('login_attempt') || undefined,
  };
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [ssoConnection, setSsoConnection] = useState<string | null>(null);
  const [showCodeForm, setShowCodeForm] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorCount, setErrorCount] = useState(0);

  const deepLinkProtocol = useMemo(
    () => resolveDeepLinkProtocol(search.state) ?? getDeepLinkProtocol(),
    [search.state]
  );

  useEffect(() => {
    if (search.email && !email) {
      setEmail(search.email);
    }
  }, [search.email, email]);

  const requestCode = useCallback(async () => {
    try {
      setIsProcessing(true);
      await sendOtpCode(auth0Config, email);
      trackEvent('AUTH0_CODE_SENT', { email });
      setSsoConnection(null);
      setShowCodeForm(true);
      setIsProcessing(false);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error sending code:', error);
      toast.error('Error sending code: ' + message);
      setIsProcessing(false);
    }
  }, [email]);

  const submitEmail = useCallback(async () => {
    if (isPasswordLoginEmail(email)) {
      setShowPasswordForm(true);
      return;
    }
    setIsProcessing(true);
    const connection = await lookupSsoConnection(email);
    setIsProcessing(false);
    if (connection) setSsoConnection(connection);
    else await requestCode();
  }, [email, requestCode]);

  const submitPassword = useCallback(
    async (password: string) => {
      try {
        setIsProcessing(true);
        const deviceId = await electronApis.system.getDeviceId();
        const tokenResponse = await signInWithPassword(
          auth0Config,
          email,
          password,
          deviceId
        );
        setLastLoginEmail(email);

        navigate({
          to: '/auth-callback/desktop',
          search: {
            code: '',
            state: '',
          },
          state: {
            tokens: tokenResponse,
            deepLinkProtocol,
            loginAttempt: search.login_attempt,
          } as never,
        });
      } catch (error: unknown) {
        console.error('Error submitting password:', error);
        toast.error(
          classifyOtpError(error).isInvalidCode
            ? 'Wrong email or password'
            : "Couldn't sign in"
        );
        setIsProcessing(false);
      }
    },
    [email, navigate, deepLinkProtocol, search.login_attempt]
  );

  const submitCode = useCallback(
    async (code: string) => {
      try {
        setIsProcessing(true);
        trackEvent('LOGIN_CODE_SUBMITTED', { code, email });

        const deviceId = await electronApis.system.getDeviceId();
        const tokenResponse = await verifyOtpCode(
          auth0Config,
          email,
          code,
          deviceId
        );

        trackEvent('AUTH0_CODE_VALID', { email, code });
        setLastLoginEmail(email);

        navigate({
          to: '/auth-callback/desktop',
          search: {
            code: '',
            state: '',
          },
          state: {
            tokens: tokenResponse,
            deepLinkProtocol,
            loginAttempt: search.login_attempt,
          } as never,
        });
      } catch (error: unknown) {
        const message =
          error instanceof Error ? error.message : 'Unknown error';
        console.error('Error submitting code:', message);
        const { isInvalidCode } = classifyOtpError(error);
        if (isInvalidCode) {
          toast.error(
            'We couldn\'t verify that code. Need a new one? Tap "Resend" and try again.'
          );
          setCode('');
          setErrorCount((c) => c + 1);
        } else {
          toast.error("Couldn't verify the code", {
            description: 'Something went wrong. Please try again in a moment.',
          });
        }
        setIsProcessing(false);
      }
    },
    [email, navigate, deepLinkProtocol, search.login_attempt]
  );

  const handleSocialLogin = useCallback(
    async (connection: string, loginHint?: string) => {
      try {
        setIsProcessing(true);
        const redirectUri = `${window.location.origin}/auth-callback/desktop`;

        await initiateSocialOAuthRedirect({
          connection,
          auth0: auth0Config,
          redirectUri,
          statePrefix: deepLinkProtocol,
          loginHint,
          loginAttempt: search.login_attempt,
          onError: () => {
            toast.error('Error initiating login');
            setIsProcessing(false);
          },
        });
      } catch (error) {
        console.error('Error initiating social OAuth:', error);
        toast.error('Error initiating login');
        setIsProcessing(false);
      }
    },
    [deepLinkProtocol, search.login_attempt]
  );

  const handleGoogleLogin = useCallback(
    () => handleSocialLogin('google-oauth2'),
    [handleSocialLogin]
  );

  const handleAppleLogin = useCallback(
    () => handleSocialLogin('apple'),
    [handleSocialLogin]
  );

  const handleMicrosoftLogin = useCallback(
    () => handleSocialLogin('microsoft'),
    [handleSocialLogin]
  );

  const handleResendCode = useCallback(() => {
    setCode('');
    trackEvent('RESEND_LOGIN_CODE');
    toast.promise(requestCode, {
      loading: 'Sending new code…',
      success: 'New code sent',
      error: "Couldn't send a new code",
    });
  }, [requestCode]);

  useTrackLoginScreenOpened();

  useEffect(() => {
    if (!showCodeForm) return;
    trackLoginCodeScreenViewed(email);
  }, [showCodeForm, email]);

  const goBackToEmail = () => {
    setCode('');
    setSsoConnection(null);
    setShowCodeForm(false);
  };

  const passwordForm = showPasswordForm ? (
    <PasswordLoginForm
      email={email}
      onSubmit={submitPassword}
      onBack={() => setShowPasswordForm(false)}
      isProcessing={isProcessing}
    />
  ) : undefined;

  return (
    <>
      <CometlyPixel />
      <MeadowAuthForm
        showSsoForm={ssoConnection !== null}
        showCodeForm={showCodeForm}
        email={email}
        setEmail={setEmail}
        onEmailSubmit={submitEmail}
        onGoogleLogin={handleGoogleLogin}
        onAppleLogin={handleAppleLogin}
        onMicrosoftLogin={handleMicrosoftLogin}
        onSsoContinue={() =>
          ssoConnection && handleSocialLogin(ssoConnection, email)
        }
        onUseCode={requestCode}
        code={code}
        setCode={setCode}
        onCodeSubmit={submitCode}
        onBack={goBackToEmail}
        refreshCode={handleResendCode}
        isProcessing={isProcessing}
        errorCount={errorCount}
        lastUsedEmail={lastUsedEmail}
        passwordStep={passwordForm}
      />
    </>
  );
}
